import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { pad, timeSlots, APPOINTMENT_TYPES, QUADRANTS } from "../utils/calendarUtils";
import { addProspect } from "../../MyTeam/services/prospectWorkflow";

const AppointmentModal = ({ isOpen, onClose, selectedDate, selectedTimeSlot, onSubmit, onDelete, editingAppointment, currentUserId }) => {
  const [formData, setFormData] = useState({
    title: "",
    type: "",
    quadrant: "",
    date: "",
    startTime: "12:00PM",
    endTime: "1:00PM",
    prospect: false,
    prospectName: "",
    prospectEmail: "",
    prospectPhone: "",
    prospectGender: "",
    prospectLocation: "",
    prospectBirthday: "",
    prospectFlags: {
      age25Plus: false,
      homeowner: false,
      solidCareer: false,
      income75kPlus: false,
      dissatisfied: false,
      entrepreneurial: false,
      spanishPreferred: false,
    },
    notes: "",
    completed: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to normalize time for comparison
  const normalizeTimeForDisplay = (timeStr) => {
    return timeStr
      .replace(/(\d+):(\d+)(AM|PM)/, '$1:$2 $3')
      .toUpperCase();
  };

  // Helper function to format time for display
  const formatTimeForDisplay = (date) => {
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return normalizeTimeForDisplay(timeStr);
  };

  useEffect(() => {
    if (isOpen) {
      if (editingAppointment) {
        console.log('Editing appointment:', editingAppointment);
        // Editing existing appointment
        const appointmentDate = new Date(editingAppointment.dateTime);
        const formattedDate = `${appointmentDate.getFullYear()}-${pad(appointmentDate.getMonth() + 1)}-${pad(appointmentDate.getDate())}`;
        
        const startTime = formatTimeForDisplay(appointmentDate);
        
        const endTime = editingAppointment.endTime ? 
          formatTimeForDisplay(new Date(editingAppointment.endTime)) : getNextTimeSlot(startTime);

        const hasProspectDetails = !!(
          editingAppointment.prospectName ||
          editingAppointment.prospectEmail ||
          editingAppointment.prospectPhone ||
          editingAppointment.prospectGender ||
          editingAppointment.prospectLocation ||
          editingAppointment.prospectBirthday ||
          (editingAppointment.prospectFlags && Object.keys(editingAppointment.prospectFlags).length)
        );

        setFormData({
          title: editingAppointment.title || "",
          type: editingAppointment.type || "",
          quadrant: editingAppointment.quadrant || "",
          date: formattedDate,
          startTime: startTime,
          endTime: endTime,
          prospect: editingAppointment.prospectId ? true : hasProspectDetails,
          prospectName: editingAppointment.prospectName || "",
          prospectEmail: editingAppointment.prospectEmail || "",
          prospectPhone: editingAppointment.prospectPhone || "",
          prospectGender: editingAppointment.prospectGender || "",
          prospectLocation: editingAppointment.prospectLocation || "",
          prospectBirthday: editingAppointment.prospectBirthday || "",
          prospectFlags: editingAppointment.prospectFlags || {
            age25Plus: false,
            homeowner: false,
            solidCareer: false,
            income75kPlus: false,
            dissatisfied: false,
            entrepreneurial: false,
            spanishPreferred: false,
          },
          notes: editingAppointment.notes || "",
          completed: editingAppointment.completed || false
        });

        // If only prospectId is present, hydrate prospect details from Firestore
        if (editingAppointment.prospectId && !hasProspectDetails) {
          (async () => {
            try {
              const ref = doc(db, "prospects", editingAppointment.prospectId);
              const snap = await getDoc(ref);
              if (snap.exists()) {
                const p = snap.data();
                const name = p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim();
                setFormData(prev => ({
                  ...prev,
                  prospect: true,
                  prospectName: name || prev.prospectName,
                  prospectEmail: p.email || prev.prospectEmail,
                  prospectPhone: p.phone || prev.prospectPhone,
                  prospectGender: p.profile?.gender || prev.prospectGender,
                  prospectLocation: p.profile?.state || prev.prospectLocation,
                  prospectBirthday: p.profile?.birthday || prev.prospectBirthday,
                  prospectFlags: p.profile?.flags || prev.prospectFlags,
                }));
              } else {
                console.warn(`Prospect ${editingAppointment.prospectId} not found`);
              }
            } catch (err) {
              console.error("Failed to load prospect details", err);
            }
          })();
        }
      } else if (selectedTimeSlot) {
        console.log('Creating new appointment at:', selectedTimeSlot);
        // Creating new appointment
        const formattedDate = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}`;
        
        const normalizedTime = normalizeTimeForDisplay(selectedTimeSlot);
        
        setFormData({
          title: "",
          type: "",
          quadrant: "",
          date: formattedDate,
          startTime: normalizedTime,
          endTime: getNextTimeSlot(normalizedTime),
          prospect: false,
          prospectName: "",
          prospectEmail: "",
          prospectPhone: "",
          prospectGender: "",
          prospectLocation: "",
          prospectBirthday: "",
          prospectFlags: {
            age25Plus: false,
            homeowner: false,
            solidCareer: false,
            income75kPlus: false,
            dissatisfied: false,
            entrepreneurial: false,
            spanishPreferred: false,
          },
          notes: "",
          completed: false
        });
      }
    }
  }, [isOpen, selectedTimeSlot, selectedDate, editingAppointment]);

  const getNextTimeSlot = (currentTime) => {
    const normalizedCurrent = normalizeTimeForDisplay(currentTime);
    const currentIndex = timeSlots.findIndex(slot => 
      normalizeTimeForDisplay(slot) === normalizedCurrent
    );
    return currentIndex < timeSlots.length - 1 ? timeSlots[currentIndex + 1] : "11:00 PM";
  };

  // Convert time picker input (HH:MM format) to 12-hour format (H:MMAM/PM)
  const convertInputToTime = (inputValue) => {
    if (!inputValue) return "12:00PM";
    const [hours, minutes] = inputValue.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${String(minutes).padStart(2, '0')}${period}`;
  };

  // Convert 12-hour format time to time picker input (HH:MM format)
  const convertTimeToInput = (timeStr) => {
    const normalized = normalizeTimeForDisplay(timeStr);
    const match = normalized.match(/(\d+):(\d+) (AM|PM)/);
    if (match) {
      const [, hour, minutes, period] = match;
      let hours24 = parseInt(hour);
      if (period === 'PM' && hours24 !== 12) hours24 += 12;
      if (period === 'AM' && hours24 === 12) hours24 = 0;
      return `${String(hours24).padStart(2, '0')}:${minutes}`;
    }
    return "12:00";
  };

  const parseTime = (timeStr) => {
    const normalized = normalizeTimeForDisplay(timeStr);
    
    // Handle 12:00 AM/PM specifically
    if (normalized === '12:00 AM') return [0, 0, 'AM'];
    if (normalized === '12:00 PM') return [12, 0, 'PM'];
    
    const match = normalized.match(/(\d+):(\d+) (AM|PM)/);
    if (match) {
      return [parseInt(match[1]), parseInt(match[2]), match[3]];
    }
    return [12, 0, 'PM']; // Default fallback
  };

  const convertTo24Hour = (hour, period) => {
    if (period === 'PM' && hour !== 12) return hour + 12;
    if (period === 'AM' && hour === 12) return 0;
    return hour;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert("Please enter a title for the appointment");
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert date and time to proper format
      const [startHour, startMinute, startPeriod] = parseTime(formData.startTime);
      const [endHour, endMinute, endPeriod] = parseTime(formData.endTime);
      
      const startDate = new Date(formData.date);
      startDate.setHours(convertTo24Hour(startHour, startPeriod));
      startDate.setMinutes(startMinute);
      startDate.setSeconds(0);
      startDate.setMilliseconds(0);
      
      const endDate = new Date(formData.date);
      endDate.setHours(convertTo24Hour(endHour, endPeriod));
      endDate.setMinutes(endMinute);
      endDate.setSeconds(0);
      endDate.setMilliseconds(0);

      const appointmentData = {
        title: formData.title.trim(),
        type: formData.type,
        quadrant: formData.quadrant,
        dateTime: startDate,
        endTime: endDate,
        prospect: formData.prospect,
        prospectId: null, // Will be set if prospect is created
        notes: formData.notes,
        completed: formData.completed
      };

      // Create prospect if needed and store prospectId
      if (formData.prospect) {
        const prospectData = {
          firstName: formData.prospectName.split(" ")[0],
          lastName: formData.prospectName.split(" ").slice(1).join(" ") || "",
          email: formData.prospectEmail.trim(),
          phone: formData.prospectPhone.trim(),
          recruiterId: currentUserId,
          profile: {
            gender: formData.prospectGender,
            state: formData.prospectLocation,
            birthday: formData.prospectBirthday,
            flags: formData.prospectFlags,
          },
          notes: `Created from calendar appointment: ${formData.title}`
        };

        console.log("Creating prospect:", prospectData);
        const { id } = await addProspect(prospectData);
        appointmentData.prospectId = id;
        console.log("Prospect created with ID:", id);
      }

      // Include the appointment ID if editing
      if (editingAppointment) {
        appointmentData.id = editingAppointment.id;
      }

      console.log('Submitting appointment:', appointmentData);
      console.log('Start time:', startDate.toString());
      console.log('End time:', endDate.toString());
      
      await onSubmit(appointmentData);
      
      // Reset form after successful submission
      setFormData({
        title: "",
        type: "",
        quadrant: "",
        date: "",
        startTime: "12:00PM",
        endTime: "1:00PM",
        prospect: false,
        prospectName: "",
        prospectEmail: "",
        prospectPhone: "",
        prospectGender: "",
        prospectLocation: "",
        prospectBirthday: "",
        prospectFlags: {
          age25Plus: false,
          homeowner: false,
          solidCareer: false,
          income75kPlus: false,
          dissatisfied: false,
          entrepreneurial: false,
          spanishPreferred: false,
        },
        notes: "",
        completed: false
      });
      
    } catch (error) {
      console.error('Error submitting appointment:', error);
      alert('Error: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content appointment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{editingAppointment ? "Edit Appointment" : "New Appointment"}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          {/* Title Input */}
          <div className="form-group">
            <label className="form-label">Appointment Title*</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="form-input"
              placeholder="Enter title..."
              required
            />
          </div>

          {/* Appointment Type */}
          <div className="form-group">
            <label className="form-label">Appointment Type*</label>
            <div className="tracker-checkbox-grid">
              {APPOINTMENT_TYPES.map(type => (
                <label key={type} className="tracker-checkbox-label">
                  <input
                    type="radio"
                    name="appointmentType"
                    value={type}
                    checked={formData.type === type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">{" "}{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Quadrant Selection */}
          <div className="form-group">
            <label className="form-label">Quadrant*</label>
            <div className="tracker-checkbox-grid quadrant-full-grid">
              {QUADRANTS.map((quadrant, index) => (
                <label key={quadrant} className="tracker-checkbox-label">
                  <input
                    type="radio"
                    name="quadrant"
                    value={quadrant}
                    checked={formData.quadrant === quadrant}
                    onChange={(e) => setFormData({...formData, quadrant: e.target.value})}
                    required
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">{" "}<strong>{index + 1}.</strong> {quadrant}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-divider"></div>

          {/* Date and Time */}
          <div className="form-group">
            <label className="form-label">Date*</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Time*</label>
            <div className="time-row">
              <div className="time-field">
                <label className="form-sublabel">Start</label>
                <input
                  type="time"
                  value={convertTimeToInput(formData.startTime)}
                  onChange={(e) => setFormData({...formData, startTime: convertInputToTime(e.target.value)})}
                  className="form-input time-picker"
                  required
                />
              </div>
              <div className="time-field">
                <label className="form-sublabel">End</label>
                <input
                  type="time"
                  value={convertTimeToInput(formData.endTime)}
                  onChange={(e) => setFormData({...formData, endTime: convertInputToTime(e.target.value)})}
                  className="form-input time-picker"
                  required
                />
              </div>
            </div>
          </div>

          {/* Add Prospect Button */}
          {!formData.prospect && (
            <button
              type="button"
              className="btn btn-add-prospect"
              onClick={() => setFormData({...formData, prospect: true})}
            >
              + Add Prospect
            </button>
          )}

          {/* Prospect Details - Show when prospect is checked */}
          {formData.prospect && (
            <div className="prospect-section">
              <div className="prospect-header">
                <label className="form-label">Prospect Details*</label>
                <button
                  type="button"
                  className="prospect-close-btn"
                  onClick={() => setFormData({...formData, prospect: false, prospectName: "", prospectEmail: "", prospectPhone: "", prospectGender: "", prospectLocation: "", prospectBirthday: "", prospectFlags: {age25Plus: false, homeowner: false, solidCareer: false, income75kPlus: false, dissatisfied: false, entrepreneurial: false, spanishPreferred: false}})}
                >
                  ×
                </button>
              </div>
              <div className="prospect-fields">
                <div className="form-group">
                  <label className="form-sublabel">Name*</label>
                  <input
                    type="text"
                    value={formData.prospectName}
                    onChange={(e) => setFormData({...formData, prospectName: e.target.value})}
                    className="form-input"
                    placeholder="Prospect name"
                    required={formData.prospect}
                  />
                </div>

                <div className="form-group">
                  <label className="form-sublabel">Email</label>
                  <input
                    type="email"
                    value={formData.prospectEmail}
                    onChange={(e) => setFormData({...formData, prospectEmail: e.target.value})}
                    className="form-input"
                    placeholder="Email address"
                  />
                </div>

                <div className="form-group">
                  <label className="form-sublabel">Phone</label>
                  <input
                    type="tel"
                    value={formData.prospectPhone}
                    onChange={(e) => setFormData({...formData, prospectPhone: e.target.value})}
                    className="form-input"
                    placeholder="Phone number"
                  />
                </div>
              </div>

              {/* Additional Fields Row */}
              <div className="prospect-additional">
                <div className="form-group">
                  <label className="form-sublabel">Gender</label>
                  <select
                    value={formData.prospectGender}
                    onChange={(e) => setFormData({...formData, prospectGender: e.target.value})}
                    className="form-input"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-sublabel">Location/State</label>
                  <input
                    type="text"
                    value={formData.prospectLocation}
                    onChange={(e) => setFormData({...formData, prospectLocation: e.target.value})}
                    className="form-input"
                    placeholder="State or Location"
                  />
                </div>

                <div className="form-group">
                  <label className="form-sublabel">Birthday</label>
                  <input
                    type="date"
                    value={formData.prospectBirthday}
                    onChange={(e) => setFormData({...formData, prospectBirthday: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Profile Flags Grid */}
              <div className="prospect-flags">
                <label className="form-label" style={{marginBottom: "12px"}}>Profile Flags</label>
                <div className="flag-grid">
                  {[
                    ["age25Plus", "25+ Y.O"],
                    ["homeowner", "Homeowner"],
                    ["solidCareer", "Solid Career Background"],
                    ["income75kPlus", "$75k+ Income"],
                    ["dissatisfied", "Dissatisfied"],
                    ["entrepreneurial", "Entrepreneurial"],
                    ["spanishPreferred", "Spanish Speaking Preferred"],
                  ].map(([key, label]) => (
                    <label key={key} className="flag-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.prospectFlags?.[key] || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          prospectFlags: {
                            ...formData.prospectFlags,
                            [key]: e.target.checked
                          }
                        })}
                      />
                      <span className="flag-checkmark"></span>
                      <span className="flag-text">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="form-textarea"
              placeholder="Add notes..."
              rows="3"
            />
          </div>

          {/* Completion Status - Only show when editing */}
          {editingAppointment && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.completed || false}
                  onChange={(e) => setFormData({...formData, completed: e.target.checked})}
                />
                <span className="checkbox-text"> Mark as completed</span>
              </label>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            {editingAppointment && onDelete && (
              <button 
                type="button" 
                className="btn btn-danger"
                onClick={() => onDelete(editingAppointment.id)}
                disabled={isSubmitting}
              >
                Delete
              </button>
            )}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={formData.prospect && !formData.prospectName.trim() || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (editingAppointment ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;